/**
 * Static option lists for the ProductScout intake wizard. Production values
 * will eventually be served by the API (Slice 2) but for Slice 1 these
 * inline lists are sufficient to demo the full intake flow against the
 * mocked store + service.
 *
 * Field names use snake_case to match the store's `intake` shape and the API
 * payload documented in `docs/modules/ProductScout/api-reference.md`.
 */

export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

/**
 * Age bands match the canonical intake schema in `data-model.md`. Bands keep
 * the prospect's exact age out of the system (privacy-by-design — agents
 * supply a range, not a DOB).
 */
export const AGE_BANDS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65-74', '75+']

export const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
]

/**
 * Health conditions — policy-qualifying questions (the common U65
 * underwriting knock-outs). Nothing selected means the prospect is healthy:
 * the wizard shows that as its "None — healthy" chip and the server tells
 * the model to treat them as healthy. Slugs reach the model with
 * underscores read as spaces.
 *
 * Built-in fallback: the live lists are admin-managed (Nova → ProductScout →
 * Wizard Options, GET /api/productscout/wizard-options).
 */
export const CONDITIONS = [
  { value: 'diabetes_type_1', label: 'Diabetes (Type 1)' },
  { value: 'diabetes_type_2', label: 'Diabetes (Type 2)' },
  { value: 'hypertension', label: 'High blood pressure' },
  { value: 'heart_disease', label: 'Heart disease, heart attack or stroke' },
  { value: 'copd_asthma', label: 'COPD, emphysema or asthma' },
  { value: 'cancer_current', label: 'Cancer — current or in treatment' },
  { value: 'cancer_history', label: 'Cancer history (in remission)' },
  { value: 'kidney_disease', label: 'Kidney disease' },
  { value: 'liver_disease', label: 'Liver disease or hepatitis' },
  { value: 'autoimmune', label: 'Autoimmune (lupus, MS, RA)' },
  { value: 'mental_health', label: 'Mental health (depression, anxiety, bipolar)' },
  { value: 'pregnancy', label: 'Pregnant or planning a pregnancy' },
  { value: 'recent_hospitalization', label: 'Hospitalized or had surgery in the last 12 months' },
  { value: 'ongoing_prescriptions', label: 'Ongoing prescription medications' },
  { value: 'high_bmi', label: 'High BMI / obesity' },
  { value: 'other_condition', label: 'Other condition (add in notes)' },
]

/**
 * Who the coverage is for — sets the plan tier (individual vs family) the
 * brochures price by. Optional, single choice.
 */
export const HOUSEHOLD = [
  { value: 'individual', label: 'Just them' },
  { value: 'with_spouse', label: 'Them + spouse' },
  { value: 'with_children', label: 'Them + child(ren)' },
  { value: 'family', label: 'Family' },
]
